import React from "react";
import cloneDeep from "lodash/cloneDeep";
import get from "lodash/get";
import camelCase from "lodash/camelCase";
import { getValueFromInput } from "util/FormsUtils";
import { Input, ControlLabel, FormControl, FormGroup, HelpBlock, InputGroup } from "components/bootstrap";
import { TimezoneSelect, URLAllowListInput, SourceCodeEditor } from "components/common";
import DocsHelper from "util/DocsHelper";
import DocumentationLink from "components/support/DocumentationLink";
class TeamsNotificationV2Form extends React.Component {
  constructor(props) {
    super(props);
    this.handleBacklogSizeChange = (event) => {
      const { name } = event.target;
      const value = getValueFromInput(event.target);
      this.setState({ [camelCase(name)]: value });
      this.propagateChange(name, getValueFromInput(event.target));
    };
    this.toggleBacklogSize = () => {
      const { isBacklogSizeEnabled, backlogSize } = this.state;
      this.setState({ isBacklogSizeEnabled: !isBacklogSizeEnabled });
      this.propagateChange("backlog_size", isBacklogSizeEnabled ? 0 : backlogSize);
    };
    this.propagateChange = (key, value) => {
      const { config, onChange } = this.props;
      const nextConfig = cloneDeep(config);
      nextConfig[key] = value;
      onChange(nextConfig);
    };
    this.handleTimeZoneChange = (nextValue) => {
      this.propagateChange("time_zone", nextValue);
    };
    this.handleWebhookUrlChange = (event) => {
      this.propagateChange("webhook_url", getValueFromInput(event.target));
    };
    this.handleAdaptiveCardChange = (nextValue) => {
      this.propagateChange("adaptive_card", nextValue);
    };
    const defaultBacklogSize = props.config.backlog_size;
    this.state = {
      isBacklogSizeEnabled: defaultBacklogSize > 0,
      backlogSize: defaultBacklogSize
    };
  }
  render() {
    const { config, validation } = this.props;
    const { isBacklogSizeEnabled, backlogSize } = this.state;
    const element = /* @__PURE__ */ React.createElement("p", null, "Adaptive Card to post to Teams. See ", /* @__PURE__ */ React.createElement(DocumentationLink, { page: DocsHelper.PAGES.ALERTS, text: "docs " }), " for more details.");
    return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
      URLAllowListInput,
      {
        label: "Webhook URL",
        onChange: this.handleWebhookUrlChange,
        validationState: validation.errors.webhook_url ? "error" : null,
        validationMessage: get(validation, "errors.webhook_url[0]", 'Teams "Incoming Webhook" URL'),
        url: config.webhook_url || "",
        autofocus: false
      }
    ), /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(ControlLabel, null, "Adaptive Card Template"), /* @__PURE__ */ React.createElement(
      SourceCodeEditor,
      {
        id: "notification-adaptiveCard",
        mode: "text",
        theme: "light",
        value: config.adaptive_card || "",
        wrapEnabled: true,
        onChange: this.handleAdaptiveCardChange
      }
    ), /* @__PURE__ */ React.createElement(HelpBlock, null, get(validation, "errors.adaptive_card[0]", element))), /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "notification-time-zone",
        help: "Time zone used for timestamps in the notification body.",
        label: "Time zone for date/time values"
      },
      /* @__PURE__ */ React.createElement(
        TimezoneSelect,
        {
          className: "timezone-select",
          name: "time_zone",
          value: config.time_zone,
          onChange: this.handleTimeZoneChange,
          clearable: false
        }
      )
    ), /* @__PURE__ */ React.createElement(ControlLabel, null, "Message Backlog Limit (optional)"), /* @__PURE__ */ React.createElement(InputGroup, null, /* @__PURE__ */ React.createElement(InputGroup.Addon, null, /* @__PURE__ */ React.createElement(
      "input",
      {
        id: "toggle_backlog_size",
        type: "checkbox",
        checked: isBacklogSizeEnabled,
        onChange: this.toggleBacklogSize
      }
    )), /* @__PURE__ */ React.createElement(
      FormControl,
      {
        type: "number",
        id: "backlog_size",
        name: "backlog_size",
        onChange: this.handleBacklogSizeChange,
        value: backlogSize,
        min: "0",
        disabled: !isBacklogSizeEnabled
      }
    )), /* @__PURE__ */ React.createElement(HelpBlock, null, "Limit the number of backlog messages sent as part of the Microsoft Teams notification. If set to 0, no limit will be enforced.")));
  }
}
TeamsNotificationV2Form.defaultConfig = {
  webhook_url: "",
  /* eslint-disable no-template-curly-in-string */
  adaptive_card: '{\n  "type": "message",\n  "attachments": [\n    {\n      "contentType": "application/vnd.microsoft.card.adaptive",\n      "content": {\n        "type": "AdaptiveCard",\n        "version": "1.5",\n        "msTeams": { "width": "full" },\n        "body": [\n          {\n            "type": "TextBlock",\n            "size": "Large",\n            "weight": "Bolder",\n            "text": "${event_definition_title} triggered",\n            "style": "heading",\n            "fontType": "Default"\n          },\n          {\n            "type": "TextBlock",\n            "text": "${event_definition_description}",\n            "wrap": true\n          },\n          {\n            "type": "TextBlock",\n            "text": "Event Details",\n            "wrap": true\n          },\n          {\n            "type": "FactSet",\n            "facts": [\n              {\n                "title": "Type",\n                "value": "${event_definition_type}"\n              },\n              {\n                "title": "Timestamp",\n                "value": "${event.timestamp_processing}"\n              },\n              {\n                "title": "Message",\n                "value": "${event.message}"\n              },\n              {\n                "title": "Source",\n                "value": "${event.source}"\n              },\n              {\n                "title": "Key",\n                "value": "${event.key}"\n              },\n              {\n                "title": "Priority",\n                "value": "${event.priority}"\n              },\n              {\n                "title": "Alert",\n                "value": "${event.alert}"\n              },\n              {\n                "title": "Timerange Start",\n                "value": "${event.timerange_start}"\n              },\n              {\n                "title": "Timerange End",\n                "value": "${event.timerange_end}"\n              }\n            ]\n          }${if event.fields},\n          {\n            "type": "TextBlock",\n            "text": "Event Fields",\n            "weight": "bolder",\n            "size": "medium"\n          },\n          {\n            "type": "FactSet",\n            "facts": [${foreach event.fields field}\n              { "title": "${field.key}", "value": "${field.value}" }${if last_field}${else},${end}${end}\n            ]\n          }${end}${if backlog},\n          {\n            "type": "TextBlock",\n            "text": "Backlog",\n            "weight": "bolder",\n            "size": "medium"\n          },\n          {\n            "type": "FactSet",\n            "facts": [${foreach backlog message}\n              { "title": "Message", "value": "${message.message}" }${if last_message}${else},${end}${end}\n            ]\n          }${end}\n        ],\n        "actions": [{\n          "type": "Action.OpenUrl",\n          "title": "Replay Search",\n          "url": "${http_external_uri}alerts/${event.id}/replay-search"\n        }],\n        "$schema": "http://adaptivecards.io/schemas/adaptive-card.json",\n        "rtl": false\n      }\n    }\n  ]\n}',
  /* eslint-enable no-template-curly-in-string */
  backlog_size: 0,
  time_zone: "UTC"
};
export default TeamsNotificationV2Form;
